/**
 * Rng.ts — a small seeded pseudo-random generator (mulberry32).
 *
 * Deterministic: the same seed always produces the same sequence. This is what
 * makes procedurally generated tracks reproducible (Tour cities get a fixed
 * seed; Quick Race gets a random one) and keeps the simulation testable.
 */
export class Rng {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0; // force to an unsigned 32-bit integer
  }

  /** Next float in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Float in [min, max). */
  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }

  /** Integer in [min, maxInclusive] (both ends inclusive). */
  int(min: number, maxInclusive: number): number {
    return Math.floor(this.range(min, maxInclusive + 1));
  }

  /** Pick a random element from a non-empty array. */
  pick<T>(arr: readonly T[]): T {
    return arr[this.int(0, arr.length - 1)];
  }
}

/**
 * Hash a string to a 32-bit seed (FNV-1a). Lets us derive a stable seed from a
 * label, e.g. a per-city seed via hashStringToSeed('tokyo').
 */
export function hashStringToSeed(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
